import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { BuilderApi, Workspace } from "../api/contract";
import { toBuilderError } from "../api/errors";
import { RequestTimeoutError, requestTimeout } from "../lib/requestTimeout";

export const builderQueryKeys = {
  workspace: (siteId: string) => ["builder", "workspace", siteId] as const,
};

/**
 * Server-owned workspace state. Refetches on focus and reconnect so a returning
 * phone always shows what PostgreSQL has, never a remembered guess.
 */
export function useWorkspace(api: BuilderApi, siteId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: builderQueryKeys.workspace(siteId),
    queryFn: ({ signal }) => requestTimeout(next => api.getWorkspace(siteId, next), signal),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (count, caught) => !(caught instanceof RequestTimeoutError) && toBuilderError(caught).outcomeUnknown && count < 2,
  });

  const refresh = useCallback(async (): Promise<Workspace | undefined> => {
    try {
      return await queryClient.fetchQuery({
        queryKey: builderQueryKeys.workspace(siteId),
        queryFn: ({ signal }) => requestTimeout(next => api.getWorkspace(siteId, next), signal),
        staleTime: 0,
        retry: false,
      });
    } catch {
      return undefined;
    }
  }, [api, queryClient, siteId]);

  return {
    workspace: query.data,
    error: query.error ? toBuilderError(query.error) : null,
    isLoading: query.isPending,
    isRefreshing: query.isFetching,
    refresh,
  };
}
